import { Controller } from "@hotwired/stimulus"
import { cloneRowFromTemplate } from "../../pwa/performance_row_builder"

// Connects to data-controller="attandances--work-edit"
export default class extends Controller {
  static targets = ["performancesContainer", "performanceTemplate"]

  open(event) {
    const btn = event.currentTarget;
    this.toggleForm(btn, true);
  }

  close(event) {
    const btn = event.currentTarget;
    const container = btn.closest(".work-item-container");
    if (container) {
      this.#updateWorkItemDisplay(container);
      this.#syncOriginalDataAttributes(container);
      container.dataset.unsaved = "true";
    }
    this.toggleForm(btn, false);
  }

  /**
   * Update the read-only work-item summary (.work-main / .work-sub) so it
   * reflects the values currently entered in the edit form.
   */
  #updateWorkItemDisplay(container) {
    const workMain = container.querySelector(".work-main");
    const workSub = container.querySelector(".work-sub");
    if (!workMain || !workSub) return;

    const projectPartSelect = container.querySelector('[data-performances--performance-field-target="projectPart"]');
    workMain.textContent = (projectPartSelect && projectPartSelect.value)
      ? this.#getSelectedOptionText(projectPartSelect)
      : "-";

    const timeSliceSelect = container.querySelector('[data-performances--performance-field-target="timeSliceSelect"]');
    const timeSliceText = (timeSliceSelect && timeSliceSelect.value)
      ? this.#getSelectedOptionText(timeSliceSelect)
      : "終日";

    const sliceStart = this.#formatTime(
      container.querySelector('[data-performances--performance-field-target="sliceStart"]')?.value
    );
    const sliceEnd = this.#formatTime(
      container.querySelector('[data-performances--performance-field-target="sliceEnd"]')?.value
    );

    const workTimeSpan = this.#formatHours(
      container.querySelector('[data-performances--performance-field-target="workTimeSpan"]')?.value
    );
    const workManDays = this.#formatDecimal(
      container.querySelector('[data-performances--performance-field-target="workManDays"]')?.value
    );
    const toolTimeSpan = this.#formatHours(
      container.querySelector('[data-performances--performance-field-target="toolTimeSpan"]')?.value
    );

    workSub.textContent = `区分:${timeSliceText}／${sliceStart} 〜 ${sliceEnd} （${workTimeSpan}h／${workManDays}人／機械${toolTimeSpan}h）`;
  }

  /**
   * After a save, the in-memory "original" baseline used by cancelPerformance
   * should be the just-saved values, not the values rendered on initial load.
   */
  #syncOriginalDataAttributes(container) {
    const targets = [
      ["originalProjectPartId", "projectPart"],
      ["originalTimeSliceName", "timeSliceSelect"],
      ["originalSliceStart", "sliceStart"],
      ["originalSliceEnd", "sliceEnd"],
      ["originalWorkTimeSpan", "workTimeSpan"],
      ["originalWorkManDays", "workManDays"],
      ["originalWorkTypeId", "workType"],
      ["originalToolId", "tool"],
      ["originalToolStartMeter", "toolStartMeter"],
      ["originalToolEndMeter", "toolEndMeter"],
      ["originalToolTimeSpan", "toolTimeSpan"],
      ["originalToolManDays", "toolManDays"],
      ["originalFuelUsage", "fuelUsage"]
    ];

    targets.forEach(([dataKey, target]) => {
      const el = container.querySelector(`[data-performances--performance-field-target="${target}"]`);
      if (el) container.dataset[dataKey] = el.value || "";
    });

    const fuelTaxExemptionCheckbox = container.querySelector('[data-performances--performance-field-target="fuelTaxExemption"]');
    if (fuelTaxExemptionCheckbox) {
      container.dataset.originalFuelTaxExemption = fuelTaxExemptionCheckbox.checked ? "true" : "false";
    }

    const timeSliceIdInput = container.querySelector('[name$="[time_slice_id]"]');
    if (timeSliceIdInput) container.dataset.originalTimeSliceId = timeSliceIdInput.value || "";

    const toolLeaseInput = container.querySelector('input[name$="[tool_lease_number]"]');
    if (toolLeaseInput) container.dataset.originalToolLeaseNumber = toolLeaseInput.value || "";
  }

  #getSelectedOptionText(select) {
    const option = select.options[select.selectedIndex];
    return option ? option.text : "";
  }

  #formatHours(value) {
    if (!value) return "0.00";
    if (typeof value === "string" && value.includes(":")) {
      const [hours, minutes] = value.split(":").map(Number);
      const total = (hours || 0) + (minutes || 0) / 60;
      return isNaN(total) ? "0.00" : total.toFixed(2);
    }
    const num = parseFloat(value);
    return isNaN(num) ? "0.00" : num.toFixed(2);
  }

  #formatDecimal(value) {
    const num = parseFloat(value);
    return isNaN(num) ? "0.00" : num.toFixed(2);
  }

  // Normalize an HTML time input value (which may include seconds/milliseconds
  // depending on what Rails rendered) to "HH:MM" for display.
  #formatTime(value) {
    if (!value) return "--:--";
    const match = String(value).match(/^(\d{2}:\d{2})/);
    return match ? match[1] : value;
  }

  toggleForm(btn, isOpen) {
    const container = btn.closest(".work-item-container");
    const editArea = container?.querySelector(".work-edit-area");

    if (editArea) {
      editArea.style.display = isOpen ? "block" : "none";
    }
  }

  /**
   * Cancel changes for a specific performance and close the edit area.
   * Resets all fields to their original values stored in data attributes.
   */
  cancelPerformance(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const wrapper = button.closest("[data-performance-wrapper]");
    if (!wrapper) return;
    
    // Reset all form fields to original values
    this.#resetPerformanceFields(wrapper);
    
    // Close the edit area
    this.toggleForm(button, false);
  }

  /**
   * Reset all fields within a performance wrapper to their original values.
   * Handles regular inputs, selects, and Choices.js enhanced selects.
   */
  #resetPerformanceFields(wrapper) {
    // Reset project_part_id (Choices.js select)
    const projectPartSelect = wrapper.querySelector('[data-performances--performance-field-target="projectPart"]');
    if (projectPartSelect) {
      const originalValue = wrapper.dataset.originalProjectPartId || "";
      this.#resetChoicesSelect(projectPartSelect, originalValue);
    }

    // Reset the time slice (name-based select) and its resolved time_slice_id.
    const timeSliceSelect = wrapper.querySelector('[data-performances--performance-field-target="timeSliceSelect"]');
    if (timeSliceSelect) {
      timeSliceSelect.value = wrapper.dataset.originalTimeSliceName || "";
    }
    const timeSliceIdInput = wrapper.querySelector('[name$="[time_slice_id]"]');
    if (timeSliceIdInput) {
      timeSliceIdInput.value = wrapper.dataset.originalTimeSliceId || "";
    }

    // Reset slice_start
    const sliceStartInput = wrapper.querySelector('[data-performances--performance-field-target="sliceStart"]');
    if (sliceStartInput) {
      const originalValue = wrapper.dataset.originalSliceStart || "";
      sliceStartInput.value = originalValue;
    }

    // Reset slice_end
    const sliceEndInput = wrapper.querySelector('[data-performances--performance-field-target="sliceEnd"]');
    if (sliceEndInput) {
      const originalValue = wrapper.dataset.originalSliceEnd || "";
      sliceEndInput.value = originalValue;
    }

    // Reset work_time_span
    const workTimeSpanInput = wrapper.querySelector('[data-performances--performance-field-target="workTimeSpan"]');
    if (workTimeSpanInput) {
      const originalValue = wrapper.dataset.originalWorkTimeSpan || "";
      workTimeSpanInput.value = originalValue;
    }

    // Reset work_man_days
    const workManDaysInput = wrapper.querySelector('[data-performances--performance-field-target="workManDays"]');
    if (workManDaysInput) {
      const originalValue = wrapper.dataset.originalWorkManDays || "";
      workManDaysInput.value = originalValue;
    }

    // Reset work_type_id (Choices.js select)
    const workTypeSelect = wrapper.querySelector('[data-performances--performance-field-target="workType"]');
    if (workTypeSelect) {
      const originalValue = wrapper.dataset.originalWorkTypeId || "";
      this.#resetChoicesSelect(workTypeSelect, originalValue);
    }

    // Reset tool_id (Choices.js select)
    const toolSelect = wrapper.querySelector('[data-performances--performance-field-target="tool"]');
    if (toolSelect) {
      const originalValue = wrapper.dataset.originalToolId || "";
      this.#resetChoicesSelect(toolSelect, originalValue);
    }

    // Reset tool_start_meter
    const toolStartMeterInput = wrapper.querySelector('[data-performances--performance-field-target="toolStartMeter"]');
    if (toolStartMeterInput) {
      toolStartMeterInput.value = wrapper.dataset.originalToolStartMeter || "0";
    }

    // Reset tool_end_meter
    const toolEndMeterInput = wrapper.querySelector('[data-performances--performance-field-target="toolEndMeter"]');
    if (toolEndMeterInput) {
      toolEndMeterInput.value = wrapper.dataset.originalToolEndMeter || "0";
    }

    // Reset tool_time_span
    const toolTimeSpanInput = wrapper.querySelector('[data-performances--performance-field-target="toolTimeSpan"]');
    if (toolTimeSpanInput) {
      const originalValue = wrapper.dataset.originalToolTimeSpan || "";
      toolTimeSpanInput.value = originalValue;
    }

    // Reset tool_man_days
    const toolManDaysInput = wrapper.querySelector('[data-performances--performance-field-target="toolManDays"]');
    if (toolManDaysInput) {
      const originalValue = wrapper.dataset.originalToolManDays || "";
      toolManDaysInput.value = originalValue;
    }

    // Reset tool_lease_number
    const toolLeaseNumberInput = wrapper.querySelector('input[name$="[tool_lease_number]"]');
    if (toolLeaseNumberInput) {
      const originalValue = wrapper.dataset.originalToolLeaseNumber || "";
      toolLeaseNumberInput.value = originalValue;
    }

    // Reset fuel_tax_exemption
    const fuelTaxExemptionCheckbox = wrapper.querySelector('[data-performances--performance-field-target="fuelTaxExemption"]');
    if (fuelTaxExemptionCheckbox) {
      fuelTaxExemptionCheckbox.checked = wrapper.dataset.originalFuelTaxExemption === "true";
    }

    // Reset fuel_usage
    const fuelUsageInput = wrapper.querySelector('[data-performances--performance-field-target="fuelUsage"]');
    if (fuelUsageInput) {
      fuelUsageInput.value = wrapper.dataset.originalFuelUsage || "0";
    }
  }

  /**
   * Reset a Choices.js enhanced select element to a specific value.
   */
  #resetChoicesSelect(selectElement, value) {
    const choicesContainer = selectElement.closest('[data-controller="choices"]');
    if (choicesContainer) {
      // Get the Choices controller instance
      const choicesController = this.application.getControllerForElementAndIdentifier(
        choicesContainer,
        "choices"
      );
      if (choicesController && typeof choicesController.setValue === 'function') {
        choicesController.setValue(value);
      } else {
        // Fallback: set value directly on the select element
        selectElement.value = value;
      }
    } else {
      selectElement.value = value;
    }
  }

  addPerformance(event) {
    const performancesContainer = this.performancesContainerTarget;
    const template = this.performanceTemplateTarget;

    if (!template || !performancesContainer) {
      console.error("Template or performances container not found");
      return;
    }

    const workItemContainer = cloneRowFromTemplate(template);

    if (!workItemContainer) {
      console.error("Failed to create performance element");
      return;
    }

    performancesContainer.appendChild(workItemContainer);

    const editButton = workItemContainer.querySelector('[data-action="click->attandances--work-edit#open"]');
    if (editButton) {
      setTimeout(() => {
        editButton.click();
      }, 100);
    }
  }

  deletePerformance(event) {
    const btn = event.currentTarget;
    const container = btn.closest(".work-item-container");
    
    if (!container) return;

    // Check if this is a new record (no id field or id is blank)
    const idInput = container.querySelector('input[name$="[id]"]');
    const destroyInput = container.querySelector('input[name$="[_destroy]"]');

    if (idInput && idInput.value && idInput.value !== "") {
      container.style.display = "none";
      if (destroyInput) {
        destroyInput.value = "true";
      }
    } else {
      container.remove();
    }
  }
}
